import React from "react"
import { MessageSquare } from "lucide-react"
import TopicPageLayout from "../topic-page-layout"
import ContactForm from "../contact-form"

const ContactSupport = () => {
  return (
    <TopicPageLayout
      title="Contact Support"
      description="Need more help? We're just a message away. Our support team is here to assist you with any questions or issues."
      icon={<MessageSquare className="h-8 w-8 text-[#3D2A60]" />}
    >
      <div className="space-y-8">
        <section>
          <h2 className="text-2xl font-semibold mb-4 text-[#3D2A60]">
            Get in Touch
          </h2>
          <p className="text-gray-600 mb-6">
            Our support team is available to help you with any questions or
            issues you may be experiencing. Please fill out the form below,
            and we'll get back to you as soon as possible.
          </p>

          <div className="bg-white shadow-sm border border-gray-100 rounded-lg p-6">
            <ContactForm />
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4 text-[#3D2A60]">
            Support Hours
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white shadow-sm border border-gray-100 rounded-lg p-6">
              <h3 className="text-lg font-medium text-[#3D2A60] mb-3">
                Email & Chat Support
              </h3>
              <p className="text-gray-600 mb-2">
                Monday to Friday: 9am - 8pm EST
              </p>
              <p className="text-gray-600">Saturday: 10am - 6pm EST</p>
              <p className="text-gray-600">Sunday: Closed</p>
            </div>

            <div className="bg-white shadow-sm border border-gray-100 rounded-lg p-6">
              <h3 className="text-lg font-medium text-[#3D2A60] mb-3">
                Emergency Support
              </h3>
              <p className="text-gray-600 mb-2">
                For urgent account security issues only:
              </p>
              <p className="text-gray-600">Available 24/7</p>
              <p className="text-gray-600 mt-2">Call: +1 (800) 123-4567</p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold mb-4 text-[#3D2A60]">
            What to Expect
          </h2>
          <div className="space-y-4">
            <div className="flex items-start">
              <div className="w-10 h-10 rounded-full bg-[#F4F0FA] flex items-center justify-center mr-4 mt-1">
                <span className="text-[#3D2A60] font-medium">1</span>
              </div>
              <div>
                <h3 className="text-lg font-medium text-[#3D2A60] mb-1">
                  Submission
                </h3>
                <p className="text-gray-600">
                  After submitting your request, you'll receive an automatic
                  confirmation email with a ticket number.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-10 h-10 rounded-full bg-[#F4F0FA] flex items-center justify-center mr-4 mt-1">
                <span className="text-[#3D2A60] font-medium">2</span>
              </div>
              <div>
                <h3 className="text-lg font-medium text-[#3D2A60] mb-1">
                  Initial Response
                </h3>
                <p className="text-gray-600">
                  Our team will respond to your request within 24 hours during
                  business days.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-10 h-10 rounded-full bg-[#F4F0FA] flex items-center justify-center mr-4 mt-1">
                <span className="text-[#3D2A60] font-medium">3</span>
              </div>
              <div>
                <h3 className="text-lg font-medium text-[#3D2A60] mb-1">
                  Resolution
                </h3>
                <p className="text-gray-600">
                  We'll work with you until your issue is resolved. Complex
                  issues may require additional time.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="w-10 h-10 rounded-full bg-[#F4F0FA] flex items-center justify-center mr-4 mt-1">
                <span className="text-[#3D2A60] font-medium">4</span>
              </div>
              <div>
                <h3 className="text-lg font-medium text-[#3D2A60] mb-1">
                  Follow-up
                </h3>
                <p className="text-gray-600">
                  After your issue is resolved, we may send a follow-up to
                  ensure everything is working properly.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <div className="bg-[#F4F0FA] p-6 rounded-lg">
            <h2 className="text-xl font-semibold mb-3 text-[#3D2A60]">
              Tips for Faster Support
            </h2>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <div className="w-5 h-5 rounded-full bg-[#E563A4] flex items-center justify-center mr-3 mt-1">
                  <span className="text-white text-xs">✓</span>
                </div>
                <p>Include your account username or email in your message</p>
              </li>
              <li className="flex items-start">
                <div className="w-5 h-5 rounded-full bg-[#E563A4] flex items-center justify-center mr-3 mt-1">
                  <span className="text-white text-xs">✓</span>
                </div>
                <p>
                  Provide detailed information about the issue you're
                  experiencing
                </p>
              </li>
              <li className="flex items-start">
                <div className="w-5 h-5 rounded-full bg-[#E563A4] flex items-center justify-center mr-3 mt-1">
                  <span className="text-white text-xs">✓</span>
                </div>
                <p>Include screenshots or screen recordings if applicable</p>
              </li>
              <li className="flex items-start">
                <div className="w-5 h-5 rounded-full bg-[#E563A4] flex items-center justify-center mr-3 mt-1">
                  <span className="text-white text-xs">✓</span>
                </div>
                <p>Mention any troubleshooting steps you've already tried</p>
              </li>
            </ul>
          </div>
        </section>
      </div>
    </TopicPageLayout>
  )
}

export default ContactSupport
